// The project board on the phone (Poulpe-881): option C of the prototype
// (mobile/board/, /mobile/board/picker), which Fabien chose. One state at a
// time, picked from a strip of the board's states with their counts, sorted
// and filtered as the desktop board is: its columns (deriveColumns), its
// filters (filterIssues), its summary's live filters (deriveSummary), under
// its query keys, so an address means the same on both. The card is the
// desktop's body and live line (CardBody, LiveLine), not its draggable Card:
// the phone reads the board and moves nothing. A card opens the phone's
// issue screen (context.tsx), whose way back returns here, in the same view.
import { useMemo } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { Issue, Priority } from '../api/types'
import { CardBody } from '../board/Card'
import { attachmentCount, landedCommitUrl, recordFactsOf } from '../board/cardFacts'
import { deployColumns } from '../board/deploy'
import { deriveColumns, type ColumnData } from '../board/filters'
import { liveOf } from '../board/live'
import { LiveLine } from '../board/LiveLine'
import { mainFailure } from '../board/MainCheckFailure'
import { deriveSummary, SUMMARY_NAMES } from '../board/Summary'
import { LIVE_FILTERS, parseView, SORT_MODES } from '../board/view'
import { CloseIcon, PriorityIcon, SearchIcon, StateIcon } from '../components/Icons'
import { surfaceNamesOf } from '../lib/releases'
import { landingsAwaitingPerson } from '../sessions/attention'
import { useBoardState } from '../store/context'
import { needsYou } from '../store/store'
import { ownColumns, PRIORITY_ORDER, priorityName, stateName } from '../vocab'
import { ProjectChoice, usePhoneProject } from './project'
import { useNeedItems } from './NeedsYou'
import { Note, Screen, TopBar } from './shared'

/** The address now, with `changes` made to its query: null or empty removes a key. */
function useQuery(): (changes: Record<string, string | null>) => string {
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  return (changes) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key)
      else next.set(key, value)
    }
    const query = next.toString()
    return `${pathname}${query ? `?${query}` : ''}`
  }
}

/** `list` with `item` added, or taken out when it was there, as a query value. */
function toggled(list: readonly string[], item: string): string | null {
  const next = list.includes(item) ? list.filter((i) => i !== item) : [...list, item]
  return next.length ? next.join(',') : null
}

export function BoardScreen() {
  const state = useBoardState()
  const { issues, labels, questions, pendingAnswers, me, sessions, landings, held, dismissed, proposals, returned, runs } = state
  const { count } = useNeedItems()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const query = useQuery()
  const view = parseView(params)
  const project = usePhoneProject()
  const selectedProjectId = project?.id ?? null
  const own = useMemo(() => [...issues.values()].filter((i) => i.project === selectedProjectId), [issues, selectedProjectId])
  const ownLabels = useMemo(() => new Map([...labels].filter(([, l]) => l.project === selectedProjectId)), [labels, selectedProjectId])
  // What needs the person, once for the summary and every card's live line.
  const needs = useMemo(() => needsYou(questions, me?.member?.id, pendingAnswers), [questions, me, pendingAnswers])
  // The desktop's summary, as BoardPage derives it: its ids are the live filters' issues.
  const summary = useMemo(() => {
    const failedMain = project ? mainFailure(project, runs.values()) : undefined
    return deriveSummary(selectedProjectId, issues, sessions, needs, landings, proposals, returned, Boolean(failedMain),
      landingsAwaitingPerson(landings, held, dismissed), project?.release_map ?? null, project?.landing_policy?.review === 'person')
  }, [project, selectedProjectId, issues, sessions, needs, landings, proposals, returned, held, dismissed, runs])
  const columns = useMemo(
    () => (view.live === 'deploy' ? deployColumns : deriveColumns)(own, view, { labels: ownLabels, live: summary.ids }, Date.now(), ownColumns(project)).filter((c) => !c.collapsed),
    // The view is the address's query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [own, ownLabels, summary, project, params.toString()],
  )
  const picked = columns.find((c) => c.key === params.get('state')) ?? columns.find((c) => c.state === 'in_progress') ?? columns[0]
  const filtered = Boolean(view.q || view.live || view.priorities.length || view.labels.length)
  const from = `${useLocation().pathname}${params.toString() ? `?${params.toString()}` : ''}`
  const top = <TopBar title="Board" back={null} brand />
  const tabs = { current: 'board' as const, count }
  if (!project) return <Screen top={top} tabs={tabs}><Note>No project yet: the board is a project's.</Note></Screen>
  return (
    <Screen top={top} tabs={tabs}>
      <ProjectChoice id="m-board-project" />
      <Filters labels={[...ownLabels.values()].filter((l) => !l.deleted_at)} counts={summary.counts} />
      <nav className="m-b-strip" aria-label="States">
        {columns.map((c) => (
          <Link key={c.key} className="m-target m-b-strip-item" to={query({ state: c.key })} replace aria-current={c.key === picked?.key ? 'true' : undefined}>
            <ColumnName column={c} /> <span className="section-count">{c.items.length}</span>
          </Link>
        ))}
      </nav>
      <div className="form-field m-b-sort">
        <label htmlFor="m-board-sort">Sort</label>
        <select id="m-board-sort" className="input m-target" value={view.sort} onChange={(e) => navigate(query({ sort: e.target.value === 'manual' ? null : e.target.value }), { replace: true })}>
          {SORT_MODES.map((m) => <option key={m.value} value={m.value}>{m.name}</option>)}
        </select>
      </div>
      {picked && (
        <section className="m-b-list" aria-label={columnName(picked)}>
          <h2 className="m-b-list-head"><ColumnName column={picked} /> <span className="section-count">{picked.items.length}</span></h2>
          {picked.items.length === 0 ? <Note>No issue in this state{filtered ? ' on these filters' : ''}.</Note> : (
            <ul className="m-items" aria-label={`${columnName(picked)}: ${picked.items.length}`}>
              {picked.items.map((issue) => <li key={issue.id}><PhoneCard issue={issue} from={from} needs={needs} /></li>)}
            </ul>
          )}
          {picked.hidden > 0 && <p className="muted">{picked.hidden} older not shown, as on the desktop board.</p>}
        </section>
      )}
    </Screen>
  )
}

function columnName(column: ColumnData): string {
  return column.column?.name ?? stateName(column.state)
}

function ColumnName({ column }: { column: ColumnData }) {
  return <><StateIcon state={column.state} /> {columnName(column)}</>
}

/**
 * The filters: a Filters button that opens the sheet (`?filters=open`), and
 * each filter that is on as a chip that removes it. The desktop's keys: `q`,
 * `priority`, `label` and `live`.
 */
function Filters({ labels, counts }: { labels: { id: string; name: string; colour: string }[]; counts: Record<(typeof LIVE_FILTERS)[number], number> }) {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const query = useQuery()
  const view = parseView(params)
  const open = params.get('filters') === 'open'
  const on: { key: string; text: string; to: string }[] = [
    ...(view.q ? [{ key: 'q', text: `“${view.q}”`, to: query({ q: null }) }] : []),
    ...(view.live ? [{ key: 'live', text: SUMMARY_NAMES[view.live], to: query({ live: null }) }] : []),
    ...view.priorities.map((p) => ({ key: `p-${p}`, text: priorityName(p), to: query({ priority: toggled(view.priorities, p) }) })),
    ...view.labels.map((l) => ({ key: `l-${l}`, text: l, to: query({ label: toggled(view.labels, l) }) })),
  ]
  return (
    <section className="m-b-filters" aria-label="Filters">
      <div className="m-b-filter-row">
        {on.length > 0 ? (
          <ul className="m-b-chips" aria-label="Filters on">
            {on.map((a) => <li key={a.key}><Link className="button summary-chip m-target" aria-pressed="true" to={a.to} replace aria-label={`Remove filter ${a.text}`}>{a.text} <CloseIcon size={12} /></Link></li>)}
          </ul>
        ) : <span className="muted m-b-none">All issues</span>}
        <Link className="button m-target m-b-filter-button" to={query({ filters: open ? null : 'open' })} replace aria-expanded={open}>
          <SearchIcon size={14} /> Filters{on.length > 0 && <span className="nav-badge" aria-label={`${on.length} on`}>{on.length}</span>}
        </Link>
      </div>
      {open && (
        <div className="m-b-sheet" role="group" aria-label="Set filters">
          <div className="form-field">
            <label htmlFor="m-board-q">Search</label>
            <input id="m-board-q" className="input m-target" type="search" value={view.q} placeholder="Key, title or description"
              onChange={(event) => navigate(query({ q: event.target.value }), { replace: true })} />
          </div>
          <fieldset className="m-b-fieldset">
            <legend>Work</legend>
            <div className="m-b-chips">
              {LIVE_FILTERS.map((f) => (
                <Link key={f} className="button summary-chip m-target" aria-pressed={view.live === f} to={query({ live: view.live === f ? null : f })} replace>
                  {SUMMARY_NAMES[f]} <b>{counts[f]}</b>
                </Link>
              ))}
            </div>
          </fieldset>
          <fieldset className="m-b-fieldset">
            <legend>Priority</legend>
            <div className="m-b-chips">
              {PRIORITY_ORDER.map((p: Priority) => (
                <Link key={p} className="button summary-chip m-target" aria-pressed={view.priorities.includes(p)} to={query({ priority: toggled(view.priorities, p) })} replace>
                  <PriorityIcon priority={p} /> {priorityName(p)}
                </Link>
              ))}
            </div>
          </fieldset>
          {labels.length > 0 && (
            <fieldset className="m-b-fieldset">
              <legend>Labels</legend>
              <div className="m-b-chips">
                {labels.map((l) => (
                  <Link key={l.id} className="button summary-chip m-target" aria-pressed={view.labels.includes(l.name.toLowerCase())} to={query({ label: toggled(view.labels, l.name.toLowerCase()) })} replace>
                    <span className="label-dot" style={{ background: l.colour }} />{l.name}
                  </Link>
                ))}
              </div>
            </fieldset>
          )}
          <div className="m-b-sheet-actions">
            <Link className="button m-target" to={query({ q: null, priority: null, label: null, labels: null, live: null })} replace>Clear filters</Link>
            <Link className="button button-primary m-target" to={query({ filters: null })} replace>Done</Link>
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * The desktop board's card, read only: its body (CardBody) opens the issue's
 * summary on the phone, and its live line (LiveLine) follows, as Card draws
 * them; nothing drags or moves it.
 */
function PhoneCard({ issue, from, needs }: { issue: Issue; from: string; needs: ReturnType<typeof needsYou> }) {
  const { labels, me, projects, entries, attachments, sessions, landings, proposals, returned } = useBoardState()
  const project = projects.get(issue.project)
  const facts = recordFactsOf(entries, issue.id)
  const counts = { comments: facts.comments, attachments: attachmentCount(attachments, issue.id) }
  const commit = landedCommitUrl(issue, facts.landed, project?.repository)
  const live = liveOf(issue, sessions, needs, landings, proposals, returned)
  return (
    <div className="card m-card m-b-card" data-priority={issue.priority}>
      <Link className="card-link m-b-card-link" to={`/${encodeURIComponent(issue.key)}?from=${encodeURIComponent(from)}`}>
        <CardBody issue={issue} labels={labels} me={me} surfaceNames={surfaceNamesOf(project)} counts={counts} />
      </Link>
      <LiveLine live={live} issue={issue} confirmed commit={commit && facts.landed ? { url: commit, sha: facts.landed } : null} />
    </div>
  )
}
